import React, { useState, useEffect, useCallback, useContext, useRef } from 'react'
import { DateTime } from 'luxon'
import DatePicker from 'react-multi-date-picker'
import 'react-multi-date-picker/styles/backgrounds/bg-dark.css'
import { toast } from 'react-toastify'
import { AuthContext } from '../../contexts/AuthContext'
import ShiftCard from './ShiftCard'
import ShiftModal from './ShiftModal'
import api from '../../services/api'

const daysShort = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab']

// Hook per rilevare mobile
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint)
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [breakpoint])
  return isMobile
}

// Italian locale for date picker
const italianLocale = {
  name: 'it',
  months: [
    ['Gennaio', 'Gen'], ['Febbraio', 'Feb'], ['Marzo', 'Mar'], ['Aprile', 'Apr'],
    ['Maggio', 'Mag'], ['Giugno', 'Giu'], ['Luglio', 'Lug'], ['Agosto', 'Ago'],
    ['Settembre', 'Set'], ['Ottobre', 'Ott'], ['Novembre', 'Nov'], ['Dicembre', 'Dic']
  ],
  weekDays: [
    ['Domenica', 'Do'], ['Lunedì', 'Lu'], ['Martedì', 'Ma'], ['Mercoledì', 'Me'],
    ['Giovedì', 'Gi'], ['Venerdì', 'Ve'], ['Sabato', 'Sa']
  ],
  digits: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
  meridiems: [['AM', 'am'], ['PM', 'pm']]
}

export default function TurnazioneCalendar({
  sectorId,
  viewMode,
  refreshKey,
  onShiftChange,
  sectors,
  users,
  canCreate
}) {
  const { hasPermission } = useContext(AuthContext)
  const isMobile = useIsMobile()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [shifts, setShifts] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingShift, setEditingShift] = useState(null)
  const [clickedDate, setClickedDate] = useState(null)
  const [existingShiftsForDay, setExistingShiftsForDay] = useState([])
  const datePickerRef = useRef(null)

  const canEdit = hasPermission('shifts_edit') || hasPermission('shifts_edit_own')
  const canDelete = hasPermission('shifts_destroy') || hasPermission('shifts_destroy_own')

  const toYMD = (date) => {
    if (!date) return ''
    if (date instanceof Date) {
      // Usa metodi locali invece di toISOString() per evitare shift di timezone
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }
    if (typeof date === 'string') {
      return date.includes('T') ? date.slice(0, 10) : date
    }
    return ''
  }

  const fetchShifts = useCallback(async () => {
    setLoading(true)
    try {
      const dt = DateTime.fromJSDate(selectedDate)
      const range = viewMode === 'month'
        ? {
            from: dt.startOf('month').startOf('week').toISODate(),
            to: dt.endOf('month').endOf('week').toISODate(),
          }
        : {
            from: dt.startOf('week').toISODate(),
            to: dt.endOf('week').toISODate(),
          }

      const params = new URLSearchParams(range)
      if (sectorId) params.append('sector_id', sectorId)

      const { data } = await api.get(`/shifts?${params}`)
      setShifts(data || [])
    } catch (err) {
      console.error('Failed to fetch shifts:', err)
      setShifts([])
    } finally {
      setLoading(false)
    }
  }, [selectedDate, viewMode, sectorId])

  useEffect(() => {
    fetchShifts()
  }, [fetchShifts, refreshKey])

  // Close date picker when modal opens
  useEffect(() => {
    if (modalOpen && datePickerRef.current) {
      datePickerRef.current.closeCalendar?.()
    }
  }, [modalOpen])

  // Navigation handlers
  const shiftPeriod = (delta) => {
    setSelectedDate(prev => {
      const n = new Date(prev)
      if (isMobile) {
        // Mobile: naviga giorno per giorno
        n.setDate(n.getDate() + delta)
      } else if (viewMode === 'month') {
        n.setMonth(n.getMonth() + delta)
      } else {
        n.setDate(n.getDate() + (delta * 7))
      }
      return n
    })
  }

  const handleDatePickerChange = (dateObj) => {
    if (dateObj) {
      const jsDate = dateObj.toDate ? dateObj.toDate() : new Date(dateObj)
      setSelectedDate(jsDate)
    }
  }

  const goToToday = () => setSelectedDate(new Date())

  const handleDayClick = (date) => {
    if (!canCreate && !canEdit) return
    const dateStr = typeof date === 'string' ? date : toYMD(date)
    const dayShifts = shifts.filter(s => s.date === dateStr)
    setExistingShiftsForDay(dayShifts)
    setClickedDate(dateStr)
    setEditingShift(null)
    setModalOpen(true)
  }

  const handleShiftClick = (shift, e) => {
    e?.stopPropagation()
    if (!canEdit) return
    const dayShifts = shifts.filter(s => s.date === shift.date)
    setExistingShiftsForDay(dayShifts)
    setEditingShift(shift)
    setClickedDate(shift.date)
    setModalOpen(true)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setEditingShift(null)
    setClickedDate(null)
    setExistingShiftsForDay([])
  }

  const handleModalSave = async (shiftData) => {
    if (editingShift) {
      await api.put(`/shifts/${editingShift.id}`, shiftData)
    } else {
      await api.post('/shifts', shiftData)
    }
    handleModalClose()
    await fetchShifts()
    onShiftChange?.()
  }

  const handleDelete = async (shiftId, e) => {
    e?.stopPropagation()
    if (!canDelete) return
    if (!window.confirm('Eliminare questo turno?')) return
    try {
      await api.delete(`/shifts/${shiftId}`)
      await fetchShifts()
      onShiftChange?.()
      toast.success('Turno eliminato', { position: 'top-center', autoClose: 2000 })
    } catch (err) {
      const msg = err.response?.data?.error || 'Errore eliminazione turno'
      toast.error(msg, { position: 'top-center' })
    }
  }

  const handleEditFromPreview = (shift) => {
    setEditingShift(shift)
  }

  const handleDeleteFromPreview = async (shiftId) => {
    if (!window.confirm('Eliminare questo turno?')) return
    try {
      await api.delete(`/shifts/${shiftId}`)
      await fetchShifts()
      // Update existing shifts for day
      setExistingShiftsForDay(prev => prev.filter(s => s.id !== shiftId))
      onShiftChange?.()
      toast.success('Turno eliminato', { position: 'top-center', autoClose: 2000 })
    } catch (err) {
      const msg = err.response?.data?.error || 'Errore eliminazione turno'
      toast.error(msg, { position: 'top-center' })
    }
  }

  const renderMonthView = () => {
    const dt = DateTime.fromJSDate(selectedDate)
    const start = dt.startOf('month').startOf('week')
    const end = dt.endOf('month').endOf('week')
    const days = []
    let day = start

    while (day <= end) {
      days.push(day.toJSDate())
      day = day.plus({ days: 1 })
    }

    const weeks = []
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7))
    }

    const today = new Date()
    const todayStr = toYMD(today)
    const currentMonth = selectedDate.getMonth()

    return (
      <div className="calendar-month">
        <div className="calendar-header">
          {daysShort.map((d, i) => (
            <div key={i} className="calendar-header-cell">{d}</div>
          ))}
        </div>
        <div className="calendar-body">
          {weeks.map((week, wi) => (
            <div key={wi} className="calendar-week">
              {week.map(d => {
                const dateStr = toYMD(d)
                const dayShifts = shifts.filter(s => s.date === dateStr)
                const isCurrentMonth = d.getMonth() === currentMonth
                const isToday = dateStr === todayStr

                return (
                  <div
                    key={dateStr}
                    className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
                    onClick={() => handleDayClick(dateStr)}
                  >
                    <span className="day-number">{d.getDate()}</span>
                    <div className="day-shifts">
                      {dayShifts.slice(0, isMobile ? 2 : 3).map(shift => {
                        const userName = shift.user?.fullName || shift.user?.email || '?'
                        const initials = userName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                        const startStr = shift.startTime?.slice(0, 5) || ''
                        const endStr = shift.endTime?.slice(0, 5) || ''
                        const sectorColor = shift.sector?.color || '#dc3545'

                        return (
                          <div
                            key={shift.id}
                            className={`shift-mini status-${shift.status}`}
                            style={{ '--sector-color': sectorColor }}
                            onClick={(e) => handleShiftClick(shift, e)}
                            title={`${userName} - ${startStr} / ${endStr}`}
                          >
                            <span className="shift-mini__avatar">{initials}</span>
                            <span className="shift-mini__time">{startStr}-{endStr}</span>
                          </div>
                        )
                      })}
                      {dayShifts.length > (isMobile ? 2 : 3) && (
                        <span className="more-shifts">+{dayShifts.length - (isMobile ? 2 : 3)}</span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    )
  }

  const renderWeekView = () => {
    const dt = DateTime.fromJSDate(selectedDate)
    const start = dt.startOf('week')
    const days = Array.from({ length: 7 }, (_, i) => start.plus({ days: i }).toJSDate())
    const hours = Array.from({ length: 16 }, (_, i) => i + 6)

    const today = new Date()
    const todayStr = toYMD(today)

    return (
      <div className="calendar-week-view">
        <div className="week-header">
          <div className="time-gutter-header"></div>
          {days.map(d => {
            const dateStr = toYMD(d)
            const isToday = dateStr === todayStr
            return (
              <div
                key={dateStr}
                className={`week-day-header ${isToday ? 'today' : ''}`}
              >
                <span className="weekday">{daysShort[d.getDay()]}</span>
                <span className="date-num">{d.getDate()}</span>
              </div>
            )
          })}
        </div>
        <div className="week-body">
          {hours.map(hour => (
            <div key={hour} className="week-row">
              <div className="time-gutter">{String(hour).padStart(2, '0')}:00</div>
              {days.map(d => {
                const dateStr = toYMD(d)
                const hourStr = String(hour).padStart(2, '0')
                const hourShifts = shifts.filter(s =>
                  s.date === dateStr &&
                  s.startTime && s.startTime.startsWith(hourStr)
                )
                return (
                  <div
                    key={`${dateStr}-${hour}`}
                    className="week-cell"
                    onClick={() => handleDayClick(dateStr)}
                  >
                    {hourShifts.map(shift => (
                      <ShiftCard
                        key={shift.id}
                        shift={shift}
                        onClick={(e) => handleShiftClick(shift, e)}
                        onDelete={(e) => handleDelete(shift.id, e)}
                        canEdit={canEdit}
                        canDelete={canDelete}
                      />
                    ))}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    )
  }

  const getPeriodLabel = () => {
    if (isMobile) {
      // Mobile: mostra giorno completo
      return selectedDate.toLocaleDateString('it-IT', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    }
    if (viewMode === 'month') {
      return selectedDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
    }
    const dt = DateTime.fromJSDate(selectedDate)
    const start = dt.startOf('week')
    const end = dt.endOf('week')
    return `${start.toFormat('d')} - ${end.toFormat('d MMMM yyyy', { locale: 'it' })}`
  }

  return (
    <div className="turnazione-calendar">
      {/* Navigation header */}
      <div className="calendar-nav">
        <div className="calendar-nav__controls">
          <button className="nav-btn nav-prev" onClick={() => shiftPeriod(-1)} title={isMobile ? 'Giorno precedente' : 'Precedente'}>
            ❮
          </button>

          <DatePicker
            ref={datePickerRef}
            value={selectedDate}
            onChange={handleDatePickerChange}
            locale={italianLocale}
            className="bg-dark"
            calendarPosition="bottom-center"
            arrow={false}
            render={(value, openCalendar) => (
              <button
                type="button"
                className="calendar-title-btn"
                onClick={openCalendar}
                title="Clicca per scegliere data"
              >
                {getPeriodLabel()}
                <i className="fas fa-caret-down" style={{ marginLeft: 8, fontSize: '0.8em' }} />
              </button>
            )}
          />

          <button className="nav-btn nav-next" onClick={() => shiftPeriod(1)} title={isMobile ? 'Giorno successivo' : 'Successivo'}>
            ❯
          </button>

          {!isMobile && (
            <button className="btn btn-outline btn-today" onClick={goToToday}>
              Oggi
            </button>
          )}
          {canCreate && (
            <button
              className="btn btn-primary"
              onClick={() => handleDayClick(toYMD(new Date()))}
            >
              <i className="fas fa-plus" />{!isMobile && ' Nuovo'}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="calendar-loading">
          <i className="fas fa-spinner fa-spin" /> Caricamento...
        </div>
      ) : (
        viewMode === 'month' ? renderMonthView() : renderWeekView()
      )}

      {modalOpen && (
        <ShiftModal
          shift={editingShift}
          date={clickedDate}
          sectorId={sectorId}
          sectors={sectors}
          users={users}
          existingShifts={existingShiftsForDay}
          onClose={handleModalClose}
          onSave={handleModalSave}
          onEditShift={handleEditFromPreview}
          onDeleteShift={handleDeleteFromPreview}
          canEdit={canEdit}
          canDelete={canDelete}
          canCreate={canCreate}
        />
      )}
    </div>
  )
}
